import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Pencil, Trash2, GripVertical, Eye, EyeOff } from 'lucide-react';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { Category } from '../../types';
import { compressImage } from '../../utils/imageCompression';
import Cookies from 'js-cookie';

const AdminCategories: React.FC = () => {
  const { categories, loading, error, addCategory, updateCategory, deleteCategory } = useData();
  const { user, isAuthenticated } = useAuth();
  const [sortedCategories, setSortedCategories] = useState<Category[]>([]);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Category>>({});
  const [showInactive, setShowInactive] = useState<boolean>(false);
  const [draggedItem, setDraggedItem] = useState<Category | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newCategory, setNewCategory] = useState<Partial<Category>>({
    name: '',
    description: '',
    active: true
  });

  // Debug auth status
  useEffect(() => {
    const token = Cookies.get('authToken');
    console.log('AdminCategories - Auth Status:', {
      isAuthenticated,
      user: user ? { email: user.email, role: user.role } : null,
      tokenExists: !!token,
      tokenPreview: token ? `${token.substring(0, 20)}...` : 'NO TOKEN'
    });
  }, [isAuthenticated, user]);

  useEffect(() => {
    // Filter categories based on showInactive
    const filtered = showInactive ? categories : categories.filter(c => c.active !== false);
    
    // Sort categories by displayOrder, then by name
    const sorted = [...filtered].sort((a, b) => {
      if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
        return a.displayOrder - b.displayOrder;
      }
      if (a.displayOrder !== undefined) return -1;
      if (b.displayOrder !== undefined) return 1;
      return a.name.localeCompare(b.name);
    });
    setSortedCategories(sorted);
  }, [categories, showInactive]);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, isNew: boolean = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const compressed = await compressImage(file);
      
      if (isNew) {
        setNewCategory({ ...newCategory, image: compressed });
      } else {
        setEditForm({ ...editForm, image: compressed });
      }
    } catch (err) {
      alert('Failed to process image. Please try a smaller image.');
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category.id);
    setEditForm(category);
  };

  const handleSave = async () => {
    if (!editingCategory || !editForm.name) return;

    try {
      await updateCategory({ ...editForm, id: editingCategory } as Category);
      setEditingCategory(null);
      setEditForm({});
    } catch (err) {
      console.error('Failed to update category:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this category? This will not affect existing products.')) {
      try {
        await deleteCategory(id);
      } catch (err) {
        console.error('Failed to delete category:', err);
      }
    }
  };

  const handleAddNew = async () => {
    if (!newCategory.name) return;

    try {
      await addCategory({
        ...newCategory,
        id: `category-${Date.now()}`,
        active: true
      } as Category);
      setShowNewForm(false);
      setNewCategory({ name: '', description: '', active: true });
    } catch (err) {
      console.error('Failed to add category:', err);
    }
  };

  const handleDragStart = (e: React.DragEvent, category: Category) => {
    setDraggedItem(category);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (!draggedItem) return;

    const draggedIndex = sortedCategories.findIndex(c => c.id === draggedItem.id);
    if (draggedIndex === dropIndex) return;

    // Create new array with reordered items
    const newCategories = [...sortedCategories];
    newCategories.splice(draggedIndex, 1);
    newCategories.splice(dropIndex, 0, draggedItem);

    // Update displayOrder for all affected categories
    const updatedCategories = newCategories.map((category, index) => ({
      ...category,
      displayOrder: index
    }));

    setSortedCategories(updatedCategories);

    // Update each category with new displayOrder
    try {
      for (const category of updatedCategories) {
        if (category.displayOrder !== categories.find(c => c.id === category.id)?.displayOrder) {
          await updateCategory(category);
        }
      }
    } catch (err) {
      console.error('Failed to update category order:', err);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading categories...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <Link to="/admin" className="inline-flex items-center text-blue-600 hover:underline mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Admin
        </Link>
        <div className="flex justify-between items-center">
          <h1 className="text-4xl font-bold">Category Management</h1>
          <button
            onClick={() => setShowNewForm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Category
          </button>
        </div>
        {error && (
          <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 text-blue-700 rounded">
          <p className="text-sm">Categories help organize products and improve navigation. Images will be displayed on the homepage.</p>
        </div>
        <div className="mt-4 flex items-center">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="mr-2 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700">Show inactive categories</span>
          </label>
          {!showInactive && categories.filter(c => c.active === false).length > 0 && (
            <span className="ml-4 text-sm text-gray-500">
              ({categories.filter(c => c.active === false).length} hidden)
            </span>
          )}
        </div>
      </div>

      {showNewForm && (
        <div className="mb-6 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-xl font-bold mb-4">Add New Category</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category Name
              </label>
              <input
                type="text"
                value={newCategory.name || ''}
                onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Banners"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <input
                type="text"
                value={newCategory.description || ''}
                onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Indoor & outdoor vinyl banners"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, true)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {newCategory.image && (
                <div className="mt-2">
                  <img
                    src={newCategory.image}
                    alt="Preview"
                    className="h-32 w-32 object-cover rounded-lg"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleAddNew}
              disabled={!newCategory.name}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
            >
              Add Category
            </button>
            <button
              onClick={() => {
                setShowNewForm(false);
                setNewCategory({ name: '', description: '', active: true });
              }}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                Order
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Image
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedCategories.map((category, index) => (
              <tr
                key={category.id}
                className={`hover:bg-gray-50 transition-all ${
                  dragOverIndex === index ? 'bg-blue-50 border-t-2 border-blue-400' : ''
                } ${category.active === false ? 'opacity-60' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, category)}
                onDragOver={(e) => handleDragOver(e, index)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, index)}
              >
                <td className="px-6 py-4">
                  <div className="flex items-center text-gray-400 hover:text-gray-600">
                    <GripVertical className="h-5 w-5" />
                  </div>
                </td>
                <td className="px-6 py-4">
                  {editingCategory === category.id ? (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleImageUpload(e, false)}
                        className="text-sm"
                      />
                      {editForm.image && (
                        <img
                          src={editForm.image}
                          alt={editForm.name}
                          className="mt-2 w-16 h-16 object-cover rounded-lg"
                        />
                      )}
                    </div>
                  ) : category.image ? (
                    <img
                      src={category.image}
                      alt={category.name}
                      className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                      <span className="text-gray-400 text-xs">No image</span>
                    </div>
                  )}
                </td>
                <td className="px-6 py-4">
                  {editingCategory === category.id ? (
                    <input
                      type="text"
                      value={editForm.name || ''}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <div className="text-lg font-medium text-gray-900">{category.name}</div>
                  )}
                </td>
                <td className="px-6 py-4">
                  {editingCategory === category.id ? (
                    <input
                      type="text"
                      value={editForm.description || ''}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  ) : (
                    <div className="text-sm text-gray-600">{category.description || '—'}</div>
                  )}
                </td>
                <td className="px-6 py-4">
                  {editingCategory === category.id ? (
                    <button
                      onClick={() => setEditForm({ ...editForm, active: !editForm.active })}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        editForm.active !== false
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {editForm.active !== false ? (
                        <>
                          <Eye className="h-3 w-3 mr-1" />
                          Active
                        </>
                      ) : (
                        <>
                          <EyeOff className="h-3 w-3 mr-1" />
                          Inactive
                        </>
                      )}
                    </button>
                  ) : (
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      category.active !== false
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {category.active !== false ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </td>
                <td className="px-6 py-4">
                  {editingCategory === category.id ? (
                    <div className="flex gap-2">
                      <button
                        onClick={handleSave}
                        className="text-green-600 hover:text-green-800"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setEditingCategory(null);
                          setEditForm({});
                        }}
                        className="text-gray-600 hover:text-gray-800"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(category)}
                        className="text-blue-600 hover:text-blue-800 p-2 rounded-lg hover:bg-blue-50 transition-colors"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(category.id)}
                        className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminCategories;